import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];

function toDateKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;
}

function buildMonthGrid(viewDate) {
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];

  for (let i = 0; i < firstDay.getDay(); i++) cells.push(null);
  for (let day = 1; day <= daysInMonth; day++) cells.push(new Date(year, month, day));

  return cells;
}

/**
 * Calendar popover anchored under the "When?" trigger. `value` is a Date|null,
 * `onChange` receives a Date|null (null clears the filter via "Any date").
 */
export default function DatePickerPopover({ open, onClose, value, onChange }) {
  const [viewDate, setViewDate] = useState(value || new Date());
  const boxRef = useRef(null);

  useEffect(() => {
    if (open) setViewDate(value || new Date());
  }, [open, value]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) onClose();
    };
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const cells = buildMonthGrid(viewDate);

  const goToMonth = (delta) => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + delta, 1));
  };

  const pickDay = (day) => {
    onChange(day);
    onClose();
  };

  return (
    <div
      ref={boxRef}
      className="absolute right-0 top-full z-50 mt-2 w-72 max-w-[calc(100vw-2rem)] rounded-2xl bg-white p-4 text-slate-900 shadow-2xl"
    >
      <div className="mb-3 flex items-center justify-between">
        <button
          type="button"
          onClick={() => goToMonth(-1)}
          className="rounded-full p-1.5 hover:bg-slate-100"
          aria-label="Previous month"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <p className="text-sm font-semibold">
          {viewDate.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
        </p>
        <button
          type="button"
          onClick={() => goToMonth(1)}
          className="rounded-full p-1.5 hover:bg-slate-100"
          aria-label="Next month"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="mb-1 grid grid-cols-7 gap-1 text-center text-xs font-medium text-muted-foreground">
        {WEEKDAYS.map((d, i) => (
          <span key={i}>{d}</span>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, i) => {
          if (!day) return <span key={i} />;
          const isPast = day < today;
          const isSelected = value && toDateKey(day) === toDateKey(value);
          const isToday = toDateKey(day) === toDateKey(today);

          return (
            <button
              key={i}
              type="button"
              disabled={isPast}
              onClick={() => pickDay(day)}
              className={`flex h-8 w-8 items-center justify-center rounded-full text-sm transition-colors ${
                isSelected
                  ? "bg-primary text-white"
                  : isPast
                  ? "cursor-not-allowed text-slate-300"
                  : isToday
                  ? "font-semibold text-primary hover:bg-primary/10"
                  : "hover:bg-slate-100"
              }`}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={() => pickDay(null)}
        className="mt-3 w-full rounded-xl border border-border py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
      >
        Any date
      </button>
    </div>
  );
}
